import React, { useRef, useState } from 'react'

const NUM_BOXES = 6

// Web variant of the OTP entry: renders the code as individual boxes to match
// the merchant-portal modal design. It still reports the value to the parent in
// the same `XXX-XXX` format the native flow uses, so the submit logic is shared.
const CardDetailsVerificationWeb = ({
  updateOtp,
  otpError,
}: {
  updateOtp: (value: string) => void
  otpError: string
}) => {
  const [chars, setChars] = useState<string[]>(Array(NUM_BOXES).fill(''))
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])

  const emitOtp = (next: string[]) => {
    const raw = next.join('')
    const formatted =
      raw.length > 3 ? `${raw.slice(0, 3)}-${raw.slice(3)}` : raw
    updateOtp(formatted)
  }

  // Fill boxes starting at `startIndex`, one character each. Handles both a
  // single typed character and a whole pasted/autofilled code.
  const fillFrom = (startIndex: number, value: string) => {
    const clean = value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .split('')

    const next = [...chars]
    let i = startIndex
    for (const char of clean) {
      if (i >= NUM_BOXES) break
      next[i] = char
      i++
    }

    setChars(next)
    emitOtp(next)

    const focusIndex = Math.min(i, NUM_BOXES - 1)
    inputsRef.current[focusIndex]?.focus()
  }

  const onChange = (index: number, value: string) => {
    const clean = value
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')

    if (!clean) {
      const next = [...chars]
      next[index] = ''
      setChars(next)
      emitOtp(next)
      return
    }

    // If more than one character arrives in a single box (e.g. autofill), spread
    // it across the following boxes rather than keeping just the last one.
    fillFrom(index, clean)
  }

  const onPaste = (
    index: number,
    e: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    e.preventDefault()
    fillFrom(index, e.clipboardData.getData('text'))
  }

  const onKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !chars[index] && index > 0) {
      inputsRef.current[index - 1]?.focus()
    }
  }

  return (
    <div className="card-details-web-verification">
      <p className="card-details-web-label">Code</p>
      <div className="card-details-web-code-boxes">
        {chars.map((char, i) => (
          <input
            key={i}
            ref={(el) => (inputsRef.current[i] = el)}
            className="card-details-web-code-box"
            type="text"
            inputMode="text"
            maxLength={1}
            value={char}
            onChange={(e) => onChange(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            onPaste={(e) => onPaste(i, e)}
          />
        ))}
      </div>
      {otpError && <span className="card-details-web-error">{otpError}</span>}
    </div>
  )
}

export default CardDetailsVerificationWeb
